<template>
  <div class="max-w-full">
    <XTable
      class="bg-base-200"
      :data="campaigns?.data || []"
      :is-loading="isLoading"
    >
      <template v-slot:header-row>
        <th>Campaign</th>
        <th>Geos</th>
        <th>Payout</th>
        <th>Status</th>
      </template>
      <template v-slot:row="{ row }">
        <td>
          <div class="flex items-center gap-6">
            <router-link
              :to="{
                name: 'Campaign',
                params: { id: row.id },
              }"
              draggable="false"
              class="block"
            >
              <div class="w-24 h-16 md:w-20 md:h-14 flex items-center justify-center">
                <img
                  v-if="row.images?.length > 0 && row.images[0].conversions.filter(image => image.conversion === '300x200').length > 0"
                  :src="
                    row.images[0].conversions.filter(
                      image => image.conversion === '300x200'
                    )[0].storageUrl
                  "
                  class="object-cover w-full h-full rounded-md border border-base-100 dark:border-transparent"
                  :alt="row.name"
                />
                <Image
                  v-else
                  sizing="contain"
                  widthClass="w-full"
                  heightClass="h-full"
                  :blank-width="20"
                  :blank-height="14"
                  :imageUrl="
                    getCampaignImageUrl(
                      row.images,
                      toggleThemeStore.activeTheme == 'night'
                        ? ['logo-dark', 'logo']
                        : ['logo', 'logo-dark'],
                      'md',
                    )
                  "
                  :thumbnailUrl="
                    getCampaignImageUrl(
                      row.images,
                      toggleThemeStore.activeTheme == 'night'
                        ? ['logo-dark', 'logo']
                        : ['logo', 'logo-dark'],
                      'md',
                    )
                  "
                  class="select-none rounded-md"
                />
              </div>
            </router-link>
            <div class="flex">
              <span class="select-none"
                ><v-icon
                  v-if="row.isFeatured"
                  v-tooltip="'Featured'"
                  class="mr-1 text-accent -rotate-6 flex-shrink inline-flex leading-relaxed"
                  name="hi-solid-badge-check"
                  scale="0.8"
                ></v-icon
              ></span>
              <span class="whitespace-normal break-words">
                <router-link
                  :to="{
                    name: 'Campaign',
                    params: { id: row.id },
                  }"
                  draggable="false"
                  class="link-primary"
                >
                  <span class="text-sm leading-5"
                    >{{ row.name }}
                    <span class="opacity-30 text-xs ml-1"
                      ><sup>#</sup>{{ row.id }}</span
                    ></span
                  >
                </router-link>
              </span>
            </div>
          </div>
        </td>
        <td>
          <div v-if="row.payouts" class="">
            <ExpandableCountriesFromPayouts
              :payouts="getPayouts(row)"
            ></ExpandableCountriesFromPayouts>
          </div>
        </td>
        <td>
          <div v-if="row.payouts" class="">
            {{ getPayoutRangeString(getPayouts(row)) ?? 'Variable CPA' }}
          </div>
        </td>
        <td>
          <router-link
            :to="{
              name: 'Campaign',
              params: { id: row.id },
            }"
            class="block cursor-pointer"
          >
            <CampaignStatus 
              :status="row.status"
              size="sm"
              class="w-full"
            />
          </router-link>
        </td>
      </template>
    </XTable>
    <Pagination
      v-if="campaigns?.paginatorInfo"
      :data="campaigns.paginatorInfo"
      :onEachSide="4"
      @load-page="loadPage"
      @next-page="nextPage"
      @previous-page="previousPage"
      @per-page="perPageSelection"
    />
  </div>
</template>

<script setup>
import { useToggleThemeStore } from "@/stores/toggleTheme"

import ExpandableCountriesFromPayouts from "@/components/common/ExpandableCountriesFromPayouts.vue"
import CampaignStatus from "@/components/common/CampaignStatus.vue"

import Pagination from "@/components/common/Pagination.vue"
import { useCampaignsStore } from "@/stores/campaigns.js"
import XTable from "@/components/common/XTable.vue"
import { getPayoutRangeString } from "@/utils/payoutRange"
import { useCampaignImages } from "@/composables/useCampaignImages.js"

const { getCampaignImageUrl } = useCampaignImages()

const toggleThemeStore = useToggleThemeStore()

const { tablePagination, updateTablePagination } = useCampaignsStore()

// const computedPagination = computed(()=>pagination)


const props = defineProps({
  campaigns: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: true,
  },
})

const getPayouts = row => {
  return row.status !== "subscribed" && row.payoutsGlobal?.length > 0
    ? row.payoutsGlobal
    : row.payouts
}

const emit = defineEmits(["loadMore"])


const backToTop = () => {
  const container = document.querySelector("#content-scroll")
  container.scrollTo({ top: 0, behavior: "smooth" }) // Example implementation
}

const loadPage = page => {
  updateTablePagination({
    ...tablePagination,
    page: page,
  })

  backToTop()
}

const nextPage = () => {
  loadPage(tablePagination.page + 1)
  backToTop()
}

const previousPage = () => {
  loadPage(tablePagination.page - 1)
  backToTop()
}

const perPageSelection = value => {
  updateTablePagination({
    ...tablePagination,
    first: value,
  })
}
</script>
